import { useEffect, useState } from "react";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, LoadingState } from "../components/states";
import { IntegrationStatusBadge, KeyValues, StatusPill, ValidationFindings } from "../components/integrations";
import {
  api,
  WORKFLOW_TYPE_NAMES,
  type IntegrationConfigValidation,
  type SafeIntegration,
  type WorkflowDefinitionView,
} from "../lib/api";

const N8N_STEPS: { title: string; body: string }[] = [
  { title: "1. Choose n8n Cloud or self-hosted", body: "n8n Cloud is fastest to start. Self-hosted gives you full control; place it behind Cloudflare Access so only ODIN and your operators can reach the editor." },
  { title: "2. Open the n8n editor URL", body: "Sign in to your n8n instance. You will import ODIN's workflow templates here." },
  { title: "3. Configure the production webhook URL", body: "This is the public URL n8n exposes for ODIN to trigger workflows. Paste it into 'Production webhook URL' below." },
  { title: "4. Set the signing secret", body: "Generate a strong signing secret and store it in ODIN's server configuration (control-api). It is used to HMAC-sign every event and callback. ODIN NEVER displays this secret back to you." },
  { title: "5. Set the ODIN callback URL", body: "n8n posts normalized data and status back to this ODIN callback URL. n8n can only submit data — it can never approve a deployment, mutate Discord, or read a credential." },
  { title: "6. Set the n8n service token", body: "The shared service token (plus a fresh timestamp and single-use nonce) authenticates n8n's transport to ODIN, in addition to the HMAC signature." },
  { title: "7. Recommend Cloudflare Access", body: "Put both the editor and the webhook behind Cloudflare Access. This keeps the automation plane private." },
  { title: "8. Import the ODIN workflows", body: "Import each allowlisted workflow template and register it below so ODIN knows the schema version it expects." },
  { title: "9. Validate health", body: "Save and validate the connection configuration. ODIN checks the URLs and records the result — nothing is sent live." },
  { title: "10. Run the test-event procedure", body: "Trigger a signed test event from n8n's side. ODIN acknowledges a valid echo only; a test event can never instruct ODIN to act." },
];

interface N8nDraft {
  displayName: string;
  publicWebhookUrl: string;
  callbackUrl: string;
}

function readDraft(config: unknown): N8nDraft {
  const c = (config ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof c[k] === "string" ? (c[k] as string) : "");
  return { displayName: str("displayName"), publicWebhookUrl: str("publicWebhookUrl"), callbackUrl: str("callbackUrl") };
}

/**
 * n8n connection guide (section 27). Shows connection status, last handshake,
 * the workflow registry, version-mismatch state, callback failures, signing
 * status and the dead-letter count.
 *
 * SECRET-SAFETY: the signing secret and service token are configured on the
 * server and are NEVER displayed here. Signing status is shown as
 * "configured / not configured" only — never the value.
 */
export function N8nConnectionView({
  integration,
  workflows,
  deadLetterCount,
  validation,
  busy,
  message,
  onSaveValidate,
  onRegisterWorkflow,
}: {
  integration: SafeIntegration | null;
  workflows: WorkflowDefinitionView[];
  deadLetterCount: number;
  validation: IntegrationConfigValidation | null;
  busy?: boolean;
  message?: string | null;
  onSaveValidate: (config: Record<string, unknown>) => void;
  onRegisterWorkflow: (workflowType: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<N8nDraft>(() => readDraft(integration?.configuration));
  const [wfType, setWfType] = useState<string>(WORKFLOW_TYPE_NAMES[0]);
  const set = <K extends keyof N8nDraft>(k: K, v: N8nDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const signingConfigured = integration?.hasCredential === true;
  const versionMismatch = workflows.filter((w) => w.state === "VERSION_MISMATCH");

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>The automation plane holds no authority.</strong>
        <span>n8n can submit normalized data and status only. It can never approve a deployment, mutate Discord, or read a credential. The signing secret is stored on the server and is never shown here.</span>
      </div>

      <div className="world-panel world-panel--current">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Connection status
        </h4>
        <KeyValues
          rows={[
            ["Status", integration ? <IntegrationStatusBadge key="s" status={integration.status} /> : <StatusPill key="s" tone="neutral" label="Not created" />],
            ["Last handshake / healthy", integration?.lastHealthyAt ? new Date(integration.lastHealthyAt).toUTCString() : "—"],
            ["Signing secret", signingConfigured ? <StatusPill key="sg" tone="ok" label="Configured (value hidden)" /> : <StatusPill key="sg" tone="warn" label="Not configured" />],
            ["Registered workflows", String(workflows.length)],
            ["Version mismatches", versionMismatch.length > 0 ? <span key="vm" className="count-chip count-chip--danger">{versionMismatch.length}</span> : <span key="vm" className="count-chip count-chip--ok">0</span>],
            ["Dead-letter / callback failures", deadLetterCount > 0 ? <span key="dl" className="count-chip count-chip--warn">{deadLetterCount}</span> : <span key="dl" className="count-chip count-chip--ok">0</span>],
          ]}
        />
      </div>

      <div className="world-panel world-panel--proposed">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Connection configuration
        </h4>
        <div className="card-grid">
          <label className="field">
            <span className="field__label">Display name</span>
            <input className="field__input" value={draft.displayName} onChange={(e) => set("displayName", e.target.value)} aria-label="n8n display name" />
          </label>
          <label className="field">
            <span className="field__label">Production webhook URL</span>
            <input className="field__input" value={draft.publicWebhookUrl} onChange={(e) => set("publicWebhookUrl", e.target.value)} aria-label="n8n production webhook URL" placeholder="https://n8n.example.com/webhook/..." />
          </label>
          <label className="field">
            <span className="field__label">ODIN callback URL</span>
            <input className="field__input" value={draft.callbackUrl} onChange={(e) => set("callbackUrl", e.target.value)} aria-label="ODIN callback URL" placeholder="https://api.odin.example.com/internal/v1/n8n/callbacks" />
          </label>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !integration || draft.publicWebhookUrl.length === 0}
          onClick={() => onSaveValidate({ ...draft, enabled: false })}
          style={{ marginTop: "0.5rem" }}
        >
          {busy ? "Working…" : "Save & validate connection"}
        </button>
        {validation ? <ValidationFindings validation={validation} /> : null}
        {message ? <p className="muted" role="status">{message}</p> : null}
        {!integration ? <p className="muted">Create an n8n integration on the Integrations page first, then return here to configure it.</p> : null}
      </div>

      <div className="world-panel world-panel--current">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Workflow registry
        </h4>
        {workflows.length === 0 ? (
          <p className="muted">No workflows registered yet. Import a template in n8n, then register its type here.</p>
        ) : (
          <table className="table" aria-label="Workflow registry">
            <thead>
              <tr className="table__head">
                <th>Workflow type</th>
                <th>Schema version</th>
                <th>State</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {workflows.map((w) => (
                <tr className="table__row" key={w.id}>
                  <td>{w.workflowType}</td>
                  <td>{w.schemaVersion}</td>
                  <td>{w.state === "VERSION_MISMATCH" ? <StatusPill tone="danger" label="VERSION_MISMATCH" /> : w.state}</td>
                  <td>{w.enabled ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="row" style={{ gap: "0.5rem", alignItems: "flex-end", marginTop: "0.5rem" }}>
          <label className="field">
            <span className="field__label">Register workflow type</span>
            <select className="field__input" value={wfType} onChange={(e) => setWfType(e.target.value)} aria-label="Workflow type to register">
              {WORKFLOW_TYPE_NAMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => onRegisterWorkflow(wfType)}>
            Register workflow
          </button>
        </div>
      </div>

      <div className="world-panel world-panel--proposed">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Guided setup
        </h4>
        <ol className="ob-checklist">
          {N8N_STEPS.map((s) => (
            <li key={s.title} style={{ marginBottom: "0.5rem" }}>
              <strong>{s.title}</strong>
              <p className="muted" style={{ margin: "0.25rem 0 0" }}>
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export function N8nConnection(): JSX.Element {
  return (
    <IntegrationPage title="n8n connection" intro={<p>Connect ODIN to your n8n automation instance. The signing secret is never displayed here.</p>}>
      {(tenantId) => <N8nPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

function N8nPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const [integration, setIntegration] = useState<SafeIntegration | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowDefinitionView[]>([]);
  const [deadLetterCount, setDeadLetterCount] = useState(0);
  const [validation, setValidation] = useState<IntegrationConfigValidation | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    Promise.all([api.integrations.list(tenantId), api.integrations.listWorkflows(tenantId), api.integrations.listDeadLetter(tenantId, { limit: 100 })])
      .then(([r, wf, dl]) => {
        setIntegration(r.integrations.find((i) => i.type === "N8N") ?? null);
        setWorkflows(wf.workflows);
        setDeadLetterCount(dl.failures.length);
        setError(null);
        setLoaded(true);
      })
      .catch((e) => {
        setError(toMessage(e));
        setLoaded(true);
      });
  };

  useEffect(() => {
    load();
    // load reads tenantId it depends on
  }, [tenantId]);

  const saveValidate = async (config: Record<string, unknown>) => {
    if (!integration) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.integrations.updateConfiguration(tenantId, integration.id, config);
      const r = await api.integrations.validate(tenantId, integration.id);
      setValidation(r.validation);
      setIntegration(r.integration);
      setMessage(r.validation.valid ? "Connection configuration validated." : `Validation found ${r.validation.errors.length} issue(s).`);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const register = async (workflowType: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await api.integrations.registerWorkflow(tenantId, workflowType);
      setMessage(`Workflow "${workflowType}" registered.`);
      load();
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (error && !loaded) return <ApiUnavailableState detail={error} />;
  if (!loaded) return <LoadingState label="Reading n8n connection…" />;

  return (
    <N8nConnectionView
      integration={integration}
      workflows={workflows}
      deadLetterCount={deadLetterCount}
      validation={validation}
      busy={busy}
      message={message}
      onSaveValidate={saveValidate}
      onRegisterWorkflow={register}
    />
  );
}
